import { useRef, useEffect, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { Template, checkTemplate, getInputFromTemplate } from "@pdfme/common";
import { Form } from "@pdfme/ui";
import {
  getFontsData,
  getBlankTemplate,
  generatePDF,
  downloadJsonFile
} from "../helper";
import { getPlugins } from '../plugins';
import { NavItem, NavBar } from "components/NavBar";
import { MReportTemplateAPI } from "api/sysadmin/m_report_template_api";
import { AReportAPI } from "api/admin/a_report_api";
import { useSelector } from "react-redux";
import { RootState } from "@custom-types/RootState";
import {Button} from "react-bootstrap";
import DataInputDialog from "components/DataInputDialog";


function FormPage() {
  const params = useParams();
  const schoolInfo = useSelector((state: RootState) => state.schoolInfo);
  const [mode, setMode] = useState(params.mode);
  const uiRef = useRef<HTMLDivElement | null>(null);
  const ui = useRef<Form | null>(null);
  const [showDataInputDialog, setShowDataInputDialog] = useState(false);

  if (!params.reportId) {
    throw new Error("reportId is required");
  }
  const reportId = params.reportId;

  const buildUi = useCallback(async () => {
    if (!uiRef.current) return;
    try {
      let template: Template = getBlankTemplate();
      let inputs;

      if (mode === "edit") {
        const report = await AReportAPI.get(reportId);
        checkTemplate(report.templateFile);
        template = report.templateFile;
        inputs = report.inputs ?? getInputFromTemplate(template);
      }
      // new
      else {
        if (!params.templateId) {
          throw new Error("templateId should not be set in new mode");
        }

        const templateInfo = await MReportTemplateAPI.get(params.templateId);
        checkTemplate(templateInfo.templateFile);
        template = templateInfo.templateFile;
        inputs = getInputFromTemplate(template);
        console.log(schoolInfo);

        // 暫定
        inputs[0]["{schoolName}"] = schoolInfo.schoolName;
        inputs[0]["{className}"] = schoolInfo.classList[0].name;
      }

      ui.current = new Form({
        domContainer: uiRef.current,
        template,
        inputs,
        options: {
          font: getFontsData(),
          lang: 'ja',
          labels: { 'signature.clear': 'Clear' },
          theme: {
            token: {
              colorPrimary: '#25c2a0',
            },
          },
        },
        plugins: getPlugins(),
      });
    } catch (ex) {
      console.log(ex);
    }
  }, []);


  const onJsonSave = async () => {
    if (ui.current) {
      downloadJsonFile(ui.current?.getInputs(), "output")
    }
  }

  const onJsonLoad = async () => {
    if (ui.current) {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".json,application/json";
      fileInput.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files[0]) {
          const file = target.files[0];
          const reader = new FileReader();
          reader.onload = (loadEvent) => {
            try {
              const jsonStr = loadEvent.target?.result;
              if (typeof jsonStr === "string") {
                const inputs = JSON.parse(jsonStr);
                
                ui.current?.setInputs(inputs);
              }
            } catch (ex) {
              toast.error("Failed to load JSON");
            }
          };
          reader.readAsText(file);
        }
      };
      fileInput.click();
    }
  }

  const onSaveInputs = async () => {
    if (ui.current) {
      const inputs = ui.current.getInputs();

      if (mode === "new") {
        await AReportAPI.add(reportId, {inputs: inputs});
        setMode("edit");
      }
      else {
        await AReportAPI.update(reportId, {inputs: inputs});
      }

      toast.success("Saved");
    }
  };

  const onResetInputs = () => {
    if (ui.current) {
      const template = ui.current.getTemplate();
      ui.current.setInputs(getInputFromTemplate(template));
    }
  };

  useEffect(() => {
    buildUi();
    return () => {
      if (ui.current) {
        ui.current.destroy();
      }
    };
  }, [uiRef, buildUi]);

  const navItems: NavItem[] = [
    {
      label: "" ,
      content: (
        <div>
          <Button onClick={onJsonSave}>
            JSONを出力
          </Button>
          <Button onClick={onJsonLoad}>
            JSONを入力
          </Button>
        </div>
      ),
    },
    {
      label: "",
      content: (
        <div>

          <Button
            onClick={onSaveInputs}>
            保存
          </Button>
          <Button
            onClick={onResetInputs}>
            リセット
          </Button>
        </div>

      ),
    },
    {
      label: "",
      content: (
        <Button
          id="generate-pdf"
          onClick={async () => {
            const startTimer = performance.now()
            await generatePDF(ui.current);
            const endTimer = performance.now()
            toast.info(`Generated PDF in ${Math.round(endTimer - startTimer)}ms ⚡️`);
          }}
        >
          PDFを出力
        </Button>
      ),
    },
    {
      label: "",
      content: (
        <Button
          onClick={() => setShowDataInputDialog(true)}
        >
          データを入力
        </Button>
      ),
    }
  ];

  return (
    <>
      <NavBar items={navItems} />
      <DataInputDialog ui={ui.current} show={showDataInputDialog} onClose={() => setShowDataInputDialog(false)} />
      <div ref={uiRef} />
    </>
  );
}

export default FormPage;
